import { useState, type ReactNode } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Copy,
  MoreVertical,
  Pencil,
  Trash2,
} from 'lucide-react';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/components/utils/utils';
import { useI18n } from '@/hooks/useI18n';
import { EmailTemplate } from '@/lib/admin/email-templates/models/email-template.model';
import { RecipientsSummary } from '@/lib/admin/email-templates/components/RecipientsSummary';
import {
  EmailTemplateSort,
  EmailTemplateSortKey,
} from '@/lib/admin/email-templates/utils/email-template-filters';

interface EmailTemplatesTableProps {
  templates: EmailTemplate[];
  isLoading: boolean;
  sort: EmailTemplateSort;
  onSortChange: (key: EmailTemplateSortKey) => void;
  onOpen: (template: EmailTemplate) => void;
  onDuplicate: (template: EmailTemplate) => void;
  onDelete: (key: string) => void;
  emptyState: ReactNode;
}

/**
 * Tabella unificata dei template: chiave, descrizione, destinatari dei due canali,
 * ultimo aggiornamento e azioni.
 *
 * I testi (oggetto email e messaggio notifica) non stanno più in tabella: sono
 * due per riga e troncati non dicevano nulla. Si leggono nella scheda, che la
 * riga apre al click.
 */
export const EmailTemplatesTable = ({
  templates,
  isLoading,
  sort,
  onSortChange,
  onOpen,
  onDuplicate,
  onDelete,
  emptyState,
}: EmailTemplatesTableProps) => {
  const { t, language } = useI18n();
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);

  const formatDate = (value: string | null): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const sortableHeader = (key: EmailTemplateSortKey, label: string) => {
    const isActive = sort.key === key;
    const Icon = !isActive ? ArrowUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown;
    return (
      <button
        type="button"
        onClick={() => onSortChange(key)}
        className={cn(
          'inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide',
          isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
        )}
      >
        <span className="truncate">{label}</span>
        <Icon className="h-3 w-3 shrink-0" />
      </button>
    );
  };

  const columns: DataTableColumn<EmailTemplate>[] = [
    {
      id: 'template_key',
      header: sortableHeader('template_key', t('emailTemplates.fields.templateKey')),
      headClassName: 'w-[28%]',
      cell: (template) => (
        <span
          className="block truncate font-mono text-xs text-gray-900"
          title={template.template_key}
        >
          {template.template_key}
        </span>
      ),
    },
    {
      id: 'description',
      header: sortableHeader('description', t('emailTemplates.fields.description')),
      headClassName: 'w-[30%]',
      cell: (template) =>
        template.description ? (
          <span className="block truncate text-sm text-gray-700" title={template.description}>
            {template.description}
          </span>
        ) : (
          <span className="text-xs italic text-gray-400">
            {t('emailTemplates.table.noDescription')}
          </span>
        ),
    },
    {
      id: 'recipients',
      header: sortableHeader('recipients', t('emailTemplates.table.recipients')),
      headClassName: 'w-[26%]',
      cell: (template) => <RecipientsSummary template={template} />,
    },
    {
      id: 'updated_at',
      header: sortableHeader('updated_at', t('emailTemplates.table.updatedAt')),
      headClassName: 'w-[16%]',
      cell: (template) => (
        <div className="leading-tight">
          <span className="block text-xs text-gray-700">{formatDate(template.updated_at)}</span>
          <span className="block text-[11px] text-gray-400">
            {t('emailTemplates.table.createdAtShort', { date: formatDate(template.created_at) })}
          </span>
        </div>
      ),
    },
  ];

  const confirmDelete = () => {
    if (templateToDelete) onDelete(templateToDelete.template_key);
    setTemplateToDelete(null);
  };

  return (
    <>
      <DataTable<EmailTemplate>
        data={templates}
        columns={columns}
        getRowId={(template) => template.template_key}
        onRowClick={onOpen}
        paginated={false}
        // table-fixed comprime le colonne: su mobile serve una larghezza minima che
        // attivi lo scroll orizzontale, su desktop il 100% resta invariato.
        tableClassName="min-w-[820px]"
        isLoading={isLoading}
        emptyState={emptyState}
        actionsHeader={t('common.actions')}
        actionsHeadClassName="w-[64px]"
        renderActions={(template) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onOpen(template)}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate(template)}>
                <Copy className="mr-2 h-4 w-4" />
                {t('common.duplicate')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setTemplateToDelete(template)}
                className="text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      />

      <AlertDialog
        open={templateToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setTemplateToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('emailTemplates.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('emailTemplates.delete.description', {
                key: templateToDelete?.template_key ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
